<template>
  <default-textual-section v-if="$store.state.displayType === 'textual'" :menu="menu" :section="section"
                           :section-modifier="sectionModifier" :menu-item-component="menuItemComponent"
                           :content-area-top-layout="('bimini-steak-item' === menuItemComponent) ? 'above-title' : ''"
                           :content-area-bottom-layout="('bimini-steak-item' === menuItemComponent) ? 'none' : ''"
                           :is-tag-legend-hidden="'bimini-steak-item' === menuItemComponent"
                           :is-title-buffer-visible="isTitleBufferVisible"
                           class="menu--section-bimini"></default-textual-section>
  <default-visual-section v-else :menu="menu" :section="section"
                          :section-modifier="sectionModifier" :menu-item-component="menuItemComponent"
                          :content-area-top-layout="('bimini-steak-item' === menuItemComponent) ? 'above-title' : ''"
                          :content-area-bottom-layout="('bimini-steak-item' === menuItemComponent) ? 'none' : ''"
                          :is-tag-legend-hidden="'bimini-steak-item' === menuItemComponent"
                          :is-title-buffer-visible="isTitleBufferVisible"
                          class="menu--section-bimini"></default-visual-section>
</template>

<script lang="ts">
/**
 * Custom component for Bimini default menu sections.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 2.0.0
 * @copyright  Copyright (C) 2023-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2023-03-22 (1.0.0) (jhoward)
 *    - Copy from Bimini_aperitif.vue component to Bimini_default.vue component.
 *
 *  2023-04-17 (1.0.1) (jhoward)
 *    - Remove unused methods from Bimini_default.vue component.
 *
 *  2023-06-19 (1.1.0) (jhoward)
 *    - Support section.ms_show_pricing_header in <Bimini_defaultSection>.
 *
 *  2023-08-23 (1.2.0) (jhoward)
 *    - Use <menu-version> in <Bimini_defaultSection>.
 *    - Place containerClasses() in computed rather than methods in <Bimini_defaultSection>.
 *
 *  2024-12-16 (1.2.1) (jhoward)
 *    - Tweak layout for highlighted box menu items in <Bimini_defaultSection>.
 *
 *  2025-02-11 (1.3.0) (jhoward)
 *    - Add <menu-schedules> to the <Bimini_defaultSection> component if in admin view.
 *
 *  2025-04-08 (2.0.0) (jhoward)
 *    - Use <default-textual-section> and <default-visual-section> in <bimini-default-section>.
 */

import {defineComponent, PropType} from "vue";
import {ALLOWED_ITEM_COMPONENTS, Menu, MENU_ITEM_TAG_LOCATIONS, MenuSection} from "../defs";
import DefaultVisualSection from "./visual/defaultVisualSection.vue";
import DefaultTextualSection from "./textual/defaultTextualSection.vue";

export default defineComponent({
  components: {
    DefaultTextualSection, DefaultVisualSection,
  },
  name: "Bimini_defaultSection",
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    menuItemComponent: {
      type: String,
      default: '',
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
    menuItemTagLocation: {
      type: String as PropType<MENU_ITEM_TAG_LOCATIONS>,
      default: ''
    },
    sectionModifier: {
      type: String,
      default: ''
    },
    isTitleBufferVisible: {
      type: Boolean,
      default: false
    },
  },
})
</script>

<style>
.textual_menu--container-bimini_steakhouse h1 {
  color: #3e2603;
  font-family: bodoni-urw, serif;
  font-weight: 700;
  font-style: normal;
  font-variant: small-caps;
  letter-spacing: 0.05em;
}

.visual_menu--container-bimini_steakhouse .visual_menu--content_top .visual_menu--section_title {
  padding: 0 0 0.4em 0;
  color: #3e2603;
  font-weight: 500;
  max-width: none;
  overflow: initial;
  position: relative;
}

@media (prefers-contrast: more) {
  .textual_menu--container-bimini_steakhouse h1,
  .visual_menu--container-bimini_steakhouse .visual_menu--content_top .visual_menu--section_title,
  .menu--section-bimini:deep(.menu--section_title),
  .menu--section-bimini:deep(.menu--section_subtitle),
  .menu--section-bimini:deep(.menu--item-text_only),
  .menu--section-bimini:deep(.menu--item-highlighted_box) {
    color: #000;
  }
  .menu--section-bimini:deep(.menu--item-highlighted_box) {
    border-color: #000;
  }
}

.visual_menu--container-bimini_steakhouse .visual_menu--content_top .visual_menu--section_title::after {
  background-color: currentColor;
  bottom: 0;
  content: "";
  height: .06em;
  left: -5em;
  position: absolute;
  right: -5em;
}
</style>

<style scoped>
.menu--section-bimini {
  margin: 0 auto;
  padding-top: 0.5em;
}

.visual_menu--section.menu--section-bimini {
  font-size: 0.9em;
}

.visual_menu--container-landscape .menu--section-bimini {
  padding-top: 0.5em;
}

.menu--section-bimini:deep(.menu--item_price) {
  text-align: right;
}

.menu--section-bimini:deep(.menu--section_title) {
  color: #3e2603;
  margin: 2rem 0 0.9em 0;
  padding: 0 1em;
  text-align: center;
  position: relative;
  overflow: initial;
  font-family: bodoni-urw, serif;
  font-weight: 700;
  font-style: normal;
  font-variant: small-caps;
  letter-spacing: 0.05em;
  font-size: 1.5em;
}

.menu--section-bimini:not(.menu--section-wine_list):deep(.menu--section_title::before) {
  position: absolute;
  content: '';
  top: 100%;
  left: 0;
  right: -2.6rem;
  height: 0.06em;
  background-color: #887963;
}

.textual_menu--section.menu--section-bimini:not(.menu--section-wine_list):deep(.menu--section_title::before) {
  right: 0;
}

.menu--section-bimini:deep(.menu--section_title .menu--section_title) {
  margin-top: 1rem;
}

.menu--section-bimini:deep(.menu--section_subtitle) {
  margin-top: -1em;
  font-size: 1em;
  font-weight: 400;
  margin-bottom: 0.4em;
  text-align: center;
  color: #3e2603;
}

.menu--section-bimini.menu--section-highlight {
  background-color: #f7f3eb;
}

.menu--section-bimini:deep(.menu--item-text_only) {
  color: #3e2603;
  font-size: 0.8em;
  margin-top: 1em;
  margin-bottom: 1em;
  grid-template-columns: 1fr;
}

.menu--section-bimini:deep(.menu--item-highlighted_box) {
  position: relative;
  border-color: #3e2603;
  width: 100%;
  max-width: 450px;
  margin-left: auto;
  margin-right: auto;
  color: #3e2603;
  text-align: center;
  display: block;
}

.menu--section-bimini:deep(.menu--item-highlighted_box ~ .menu--item-highlighted_box) {
  margin-top: 2em;
}

.menu--section-bimini:deep(.menu--item-highlighted_box .menu--item_description) {
  font-size: 0.9em;
  line-height: 1.2em;
}
</style>